<template>
  <BContent
    v-loading="loading"
  >
    <template #navbar>
      <BLayout
        align-start
        justify-start
      >
        <span class="fs-400 fw-bold">{{ i18n.t('training.title') }}</span>
      </BLayout>
    </template>
    <template #waiting>
      <BPlaceholder
        v-loading="true"
        element-loading-background="transparent"
      />
    </template>
    <BTabs
      v-model="activeName"
      class="tabs"
      :label-items="tabLabelItems"
    >
      <template #callFb>
        <SisenseContextProvider
          :url="sisenseUrl"
          :app-config="sisenseAppConfig"
          sso-enabled
        >
          <template v-if="sisenseAuthChecked">
            <CallFb
              v-model:caller-ids="callerIds"
              v-model:start-date="startDate"
              v-model:end-date="endDate"
              v-model:default-call-result-type-ids="defaultCallResultTypeIds"
              v-model:custom-call-result-type-ids="customCallResultTypeIds"
              v-model:call-connection-points="callConnectionPoints"
              v-model:call-result-id="callResultId"
            />
          </template>
        </SisenseContextProvider>
      </template>
    </BTabs>
  </BContent>
</template>

<script lang="ts" setup>
import {
  SisenseContextProvider,
} from '@sisense/sdk-ui-vue';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import CallFb from '@/components/organisms/user/callFb/CallFb.vue';
import { useQueryParameter } from '@/composable/query-parameter';
import { useSisense } from '@/composable/user/sisense/sisense';

const { sisenseAuthChecked } = useSisense();

const sisenseUrl = `${import.meta.env.VITE_SISENSE_URL}`;
const sisenseAppConfig = {
  dateConfig: {
    timeZone: 'JST',
  },
};

const i18n = useI18n();
const { param: callerIdsParam } = useQueryParameter<number[]>('callerIds', (param) => Array.isArray(param) ? param.map(Number) : param ? [Number(param)] : []);
const { param: startDateParam } = useQueryParameter<string>('startDate');
const { param: endDateParam } = useQueryParameter<string>('endDate');
const { param: defaultCallResultTypeIdsParam } = useQueryParameter<number[]>('defaultCallResultTypeIds', (param) => Array.isArray(param) ? param.map(Number) : param ? [Number(param)] : []);
const { param: customCallResultTypeIdsParam } = useQueryParameter<number[]>('customCallResultTypeIds', (param) => Array.isArray(param) ? param.map(Number) : param ? [Number(param)] : []);
const { param: callConnectionPointsParam } = useQueryParameter<string[]>('callConnectionPoints', (param) => Array.isArray(param) ? param : param ? [param] : []);
const { param: callResultIdParam } = useQueryParameter<number>('callResultId', (param) => Number(param) || null);

// NOTE: 「すべて〜」など未選択状態となった時に、クエリパラメータから消す。
const callerIds = computed({
  get: () => callerIdsParam.value,
  set: (value: number[] | undefined) => {
    callerIdsParam.value = value ?? undefined;
  },
});
const startDate = computed({
  get: () => startDateParam.value,
  set: (value: string | undefined) => {
    startDateParam.value = value ?? undefined;
  },
});
const endDate = computed({
  get: () => endDateParam.value,
  set: (value: string | undefined) => {
    endDateParam.value = value ?? undefined;
  },
});
const defaultCallResultTypeIds = computed({
  get: () => defaultCallResultTypeIdsParam.value,
  set: (value: number[] | undefined) => {
    defaultCallResultTypeIdsParam.value = value ?? undefined;
  },
});
const customCallResultTypeIds = computed({
  get: () => customCallResultTypeIdsParam.value,
  set: (value: number[] | undefined) => {
    customCallResultTypeIdsParam.value = value ?? undefined;
  },
});
const callConnectionPoints = computed({
  get: () => callConnectionPointsParam.value,
  set: (value: string[] | undefined) => {
    callConnectionPointsParam.value = value ?? undefined;
  },
});
const callResultId = computed({
  get: () => callResultIdParam.value,
  set: (value: number | null) => {
    callResultIdParam.value = value ?? undefined;
  },
});

const activeName = ref('callFb');
const loading = computed(() => false);

const tabLabelItems = [
  { label: i18n.t('training.callFb.title'), slotName: 'callFb' },
];
</script>

<style lang="scss" scoped>
.tabs {
  height: 100%;
}

:deep(.el-tabs__header) {
  margin-bottom: 0;

  .el-tabs__nav-scroll {
    padding: 0 $basespace-600;
  }
}

:deep(.el-tabs__content) {
  // NOTE: header分引く
  height: calc(100% - 40px);

  .el-tab-pane {
    height: 100%;
  }
}
</style>
